import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./auth";

/**
 * Perspektiven: Das System bedient zwei Zielgruppen mit teils anderen Sichten.
 *  - "berater" (Impulse): volle Steuerung - alle Ressorts, Driver-Tree, Trust,
 *    Briefing erzeugen, Export für den Kundentermin.
 *  - "kunde" (1&1 Management): verdichtetes Ampel-Cockpit - "Wo stehen wir?".
 *
 * Die Rolle kommt aus Keycloak: platform_admin -> Berater, sonst Kunde.
 * Ein Berater darf die Kundensicht als Vorschau einschalten (Umschalter im Kopf),
 * damit er sieht, was der Kunde im Steuerkreis sieht. Ein Kunde kann NICHT in die
 * Beratersicht wechseln (kein Rechte-Upgrade über die UI).
 */
export type Perspective = "berater" | "kunde";

const PLATFORM_ADMIN = "platform_admin";
const STORAGE_KEY = "kpi.perspective.override";

type PerspectiveCtx = {
  perspective: Perspective;
  autoPerspective: Perspective;
  isBerater: boolean;
  /** Nur Berater duerfen die Sicht umschalten (Kundensicht als Vorschau). */
  canChoose: boolean;
  overriding: boolean;
  setPerspective: (p: Perspective) => void;
  resetPerspective: () => void;
};

const Ctx = createContext<PerspectiveCtx | null>(null);

export function derivePerspective(roles: string[]): Perspective {
  return roles.includes(PLATFORM_ADMIN) ? "berater" : "kunde";
}

export function PerspectiveProvider({ children }: { children: ReactNode }) {
  const { roles } = useAuth();
  const autoPerspective = derivePerspective(roles);
  const canChoose = autoPerspective === "berater";
  const [override, setOverride] = useState<Perspective | null>(() => {
    if (typeof window === "undefined") return null;
    const v = window.sessionStorage.getItem(STORAGE_KEY);
    return v === "berater" || v === "kunde" ? (v as Perspective) : null;
  });

  const perspective: Perspective = canChoose ? override ?? autoPerspective : autoPerspective;

  const setPerspective = (p: Perspective) => {
    if (!canChoose) return;
    setOverride(p);
    if (typeof window !== "undefined") window.sessionStorage.setItem(STORAGE_KEY, p);
  };
  const resetPerspective = () => {
    setOverride(null);
    if (typeof window !== "undefined") window.sessionStorage.removeItem(STORAGE_KEY);
  };

  const value = useMemo<PerspectiveCtx>(
    () => ({
      perspective,
      autoPerspective,
      isBerater: perspective === "berater",
      canChoose,
      overriding: canChoose && override != null && override !== autoPerspective,
      setPerspective,
      resetPerspective,
    }),
    [perspective, autoPerspective, canChoose, override],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePerspective(): PerspectiveCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePerspective outside PerspectiveProvider");
  return v;
}